import { useEffect, useRef, useState, type ChangeEvent } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { askUrl, type Message, type Session } from "../api";
import { ask, type PipelineProgress } from "../sse";
import { uploadToNexus } from "../felix";
import { glyphOf, stepOf } from "../steps";
import {
  CopyButton, Pill, TaskCard, ThinkingTrace, type RunState,
} from "./ai/Primitives";
import {
  IconPage, IconPaperclip, IconSend, IconSparkle, IconSpinner, IconX,
} from "./icons";
import {
  sessionKey, updateChat, updatePanel, useChat,
  type PendingTool,
} from "../sessionStore";

// How close to the bottom still counts as "stuck" (px). Scrolling up past this
// releases the auto-scroll so a streaming answer can be read back.
const STICK_PX = 80;

// Openers on a fresh session — they show what the clerk is for by example,
// which no amount of explanatory copy would do as well.
const STARTERS = [
  "How does the makeup quota work?",
  "What happens to bookings during a pause period?",
  "How does double progression pick the next weight?",
];


/**
 * Chat pane for one session. Every piece of mutable state — transcript, draft,
 * attachments, the in-flight run — lives in the per-session store keyed by
 * `key`, and the send loop writes through that key. Switching sessions mid-answer
 * therefore leaves the answer streaming into its own session and hands you a
 * clean composer in the new one.
 */
export function ChatView({
  instance, session, onCitationClick, onAfterAsk,
}: {
  instance: string;
  session: Session;
  onCitationClick: (pageId: string, anchor?: string) => void;
  onAfterAsk: () => void;
}) {
  const key = sessionKey(instance, session.id);
  const {
    messages, draft: question, attached, uploading,
    busy, streaming, tools: streamingTools, progress: pipelineProgress,
    error: err,
  } = useChat(key);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Sticky-bottom scroll. `stickRef` is the synchronous "auto-scroll on" flag
  // the pin effect reads; the scroll handler flips it the moment the user
  // scrolls away from the bottom (and back). `showJump` drives the
  // jump-to-latest button shown while detached.
  const stickRef = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_PX;
    stickRef.current = atBottom;
    setShowJump(!atBottom);
  };

  const jumpToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickRef.current = true;
    setShowJump(false);
  };

  useEffect(() => {
    stickRef.current = true;     // opening a session pins to its latest message
    setShowJump(false);
  }, [key]);

  // Clock behind the live trace — restarted whenever this session starts waiting.
  const [startedAt, setStartedAt] = useState(() => Date.now());
  useEffect(() => { if (busy) setStartedAt(Date.now()); }, [busy, key]);

  // Pin to the bottom as content streams in — but only while stuck, so a
  // scrolled-up reader is never yanked back down.
  useEffect(() => {
    if (!stickRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming, streamingTools, pipelineProgress]);

  const uploadFiles = async (files: File[]) => {
    if (!files.length || busy) return;
    updateChat(key, { uploading: true, error: null });
    try {
      const up = await uploadToNexus(instance, files);
      updateChat(key, s => ({ attached: [...s.attached, ...up] }));
    } catch (e) {
      updateChat(key, { error: `upload failed: ${(e as Error).message}` });
    } finally {
      updateChat(key, { uploading: false });
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onAttach = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFiles(Array.from(e.target.files || []));
  };

  // Drag a file anywhere over the chat to attach it.
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };
  const onDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
      e.preventDefault();               // required so the drop event fires
      if (!dragOver) setDragOver(true);
    }
  };
  const onDragLeave = (e: React.DragEvent) => {
    // ignore leave events into child elements (avoids flicker)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
  };

  const send = async (q: string) => {
    if (!q || busy) return;
    const sent = attached;                 // files ride this turn; capture before clearing
    const fileKeys = sent.map(a => a.key);
    // `runKey` is captured for the whole stream: every callback below writes to
    // the session that asked, even after the user navigates elsewhere.
    const runKey = key;
    updateChat(runKey, {
      busy: true,
      error: null,
      draft: "",
      attached: [],              // chips clear the instant you send — they move into the bubble
      streaming: "",
      tools: [],
      progress: null,
    });
    stickRef.current = true;     // re-engage: follow the answer you just asked for
    setShowJump(false);

    // Optimistic user message — carries the attachments so the files visibly
    // land in the conversation, not stuck in the composer.
    const optimistic: Message = {
      id: -Date.now(),
      session_id: session.id,
      role: "user",
      content: q,
      tool_calls: [],
      citations: [],
      tokens_in: 0,
      tokens_out: 0,
      created_at: new Date().toISOString(),
      attachments: sent.map(a => ({ name: a.name })),
    };
    updateChat(runKey, s => ({ messages: [...s.messages, optimistic] }));

    try {
      let buf = "";
      const tools: PendingTool[] = [];
      await ask(askUrl(instance, session.id), q, {
        onText: (d) => { buf += d; updateChat(runKey, { streaming: buf }); },
        onToolCall: (call) => {
          tools.push({ ...call, status: "running" });
          updateChat(runKey, { tools: [...tools] });
        },
        onToolResult: (r) => {
          // Resolve the oldest still-running call of that tool.
          const t = tools.find(x => x.name === r.name && x.status === "running");
          if (t) t.status = r.ok ? "done" : "error";
          updateChat(runKey, { tools: [...tools] });
          if (r.name === "run_pipeline") updateChat(runKey, { progress: null });
        },
        onPipelineProgress: (p) => updateChat(runKey, { progress: p }),
        // The run belongs to the session that started it — open its pipeline
        // window there, not in whatever session happens to be on screen.
        onPipelineRun: (run) => updatePanel(runKey, { run, runOpen: true }),
        onFinal: (final) => {
          // Replace streaming buffer with the final canonical message
          const assistantMsg: Message = {
            id: -1,
            session_id: session.id,
            role: "assistant",
            content: final.answer,
            tool_calls: tools.map(t => ({
              name: t.name, input: t.input, result_summary: "", ok: t.status !== "error",
            })),
            citations: final.citations,
            tokens_in: final.tokens_in,
            tokens_out: final.tokens_out,
            created_at: new Date().toISOString(),
          };
          updateChat(runKey, s => ({
            messages: [...s.messages, assistantMsg],
            streaming: "",
            tools: [],
            progress: null,
          }));
        },
      }, undefined, fileKeys);
      onAfterAsk();
    } catch (e) {
      updateChat(runKey, { error: (e as Error).message });
    } finally {
      updateChat(runKey, { busy: false });
    }
  };

  const submit = () => send(question.trim());

  return (
    <div className={`chat-dropzone${dragOver ? " drag-over" : ""}`}
         onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
      <div className="messages-wrap">
        <div className="messages" ref={scrollRef} onScroll={handleScroll}>
          {messages.length === 0 && !busy && !streaming && (
            <div className="empty-state">
              <span className="empty-mark"><IconSparkle size={24} /></span>
              <h2>What do you want to know about Vigeo?</h2>
              <div className="empty-prompts">
                {STARTERS.map(s => (
                  <button key={s} className="followup" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageItem
              key={`${m.id}-${i}`}
              message={m}
              onCitationClick={onCitationClick}
            />
          ))}
          {(busy || streaming) && (
            <StreamingMessage
              text={streaming}
              tools={streamingTools}
              progress={pipelineProgress}
              since={startedAt}
              onCitationClick={onCitationClick}
            />
          )}
          {err && <div className="error-banner">{err}</div>}
        </div>
        {showJump && (
          <button className="jump-bottom" onClick={jumpToBottom}
                  title="Jump to latest" aria-label="Jump to latest">
            ↓
          </button>
        )}
      </div>

      <div className="composer">
        {/* One bordered surface: chips, field and actions read as a single control. */}
        <div className="prompt-bar">
          {attached.length > 0 && (
            <div className="composer-attachments">
              {attached.map((a, i) => (
                <span key={a.key} className="attach-chip">
                  <span className="attach-chip-icon" aria-hidden="true"><IconPaperclip size={13} /></span>
                  <span className="attach-chip-name" title={a.name}>{a.name}</span>
                  <button className="attach-chip-remove"
                          onClick={() => updateChat(key, s => ({ attached: s.attached.filter((_, j) => j !== i) }))}
                          disabled={busy} title="Remove" aria-label={`Remove ${a.name}`}>
                    <IconX size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* The textarea stays live while the clerk works: you can draft the next
              question here, or leave for another session and come back to it. */}
          <textarea
            value={question}
            rows={1}
            onChange={e => updateChat(key, { draft: e.target.value })}
            onKeyDown={e => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={busy ? "Waiting for the clerk — draft your next question…" : "Ask a question"}
          />
          <div className="prompt-actions">
            <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={onAttach} />
            <button className="icon-btn" onClick={() => fileRef.current?.click()} disabled={busy || uploading}
              title="Attach files — or drag them onto the chat" aria-label="Attach files">
              {uploading ? <IconSpinner size={16} /> : <IconPaperclip size={16} />}
            </button>
            <div className="spacer" />
            <span className="prompt-hint">⌘↩</span>
            <button className="primary send-btn" onClick={submit} disabled={busy || !question.trim()}
                    title="Send" aria-label="Send">
              {busy ? <IconSpinner size={16} /> : <IconSend size={16} />}
            </button>
          </div>
        </div>
      </div>

      {dragOver && (
        <div className="chat-drop-overlay">
          <div className="chat-drop-card">
            <IconPaperclip size={16} /> Drop files to attach
          </div>
        </div>
      )}
    </div>
  );
}


function MessageItem({
  message, onCitationClick,
}: {
  message: Message;
  onCitationClick: (pageId: string, anchor?: string) => void;
}) {
  const assistant = message.role === "assistant";
  return (
    <div className={`message ${message.role}`}>
      <span className="msg-avatar" aria-hidden="true">{assistant ? "AI" : "You"}</span>
      <div className="msg-body">
        <div className="role">{assistant ? "Clerk" : "You"}</div>
        {message.tool_calls.length > 0 && (
          <ToolTrace tools={message.tool_calls.map(t => ({
            name: t.name, input: t.input,
            status: t.ok === false ? "error" as const : "done" as const,
          }))} />
        )}
        <div className="content">
          {assistant ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents(onCitationClick)}>
              {message.content}
            </ReactMarkdown>
          ) : (
            message.content
          )}
        </div>
        {message.attachments && message.attachments.length > 0 && (
          <div className="message-attachments">
            {message.attachments.map((a, i) => (
              <span key={i} className="attach-chip readonly">
                <span className="attach-chip-icon" aria-hidden="true"><IconPaperclip size={13} /></span>
                <span className="attach-chip-name" title={a.name}>{a.name}</span>
              </span>
            ))}
          </div>
        )}
        {assistant && message.citations && message.citations.length > 0 && (
          <SourceStrip citations={message.citations} onCitationClick={onCitationClick} />
        )}
        {assistant && message.content && (
          <div className="msg-actions">
            <CopyButton text={message.content} />
          </div>
        )}
      </div>
    </div>
  );
}


/** What the answer stands on — the cited synapse pages, as openable pills. */
function SourceStrip({
  citations, onCitationClick,
}: {
  citations: string[];
  onCitationClick: (pageId: string, anchor?: string) => void;
}) {
  // One pill per page, in first-cited order; a section anchor folds into its page.
  const seen = new Set<string>();
  const pages: string[] = [];
  for (const c of citations) {
    const id = c.replace(/^#/, "").split("#")[0];
    if (!id || seen.has(id)) continue;
    seen.add(id);
    pages.push(id);
  }
  if (!pages.length) return null;
  return (
    <div className="source-strip">
      <span className="source-strip-label">
        {pages.length} source{pages.length === 1 ? "" : "s"}
      </span>
      {pages.map(id => (
        <button
          key={id}
          className="source-pill"
          onClick={() => onCitationClick(id)}
          title={id}
        >
          <span className="src-kind" aria-hidden="true"><IconPage size={11} /></span>
          <span className="src-text">{id.split("/").pop() || id}</span>
        </button>
      ))}
    </div>
  );
}


function StreamingMessage({
  text, tools, progress, since, onCitationClick,
}: {
  text: string;
  tools: PendingTool[];
  progress: PipelineProgress | null;
  since: number;
  onCitationClick: (pageId: string, anchor?: string) => void;
}) {
  // The loader and the shimmer already say "in progress", so the label is just
  // what is being done — no "Running Run pipeline" stutter.
  const runningTool = tools.find(t => t.status === "running");
  const label = runningTool
    ? (runningTool.name === "run_pipeline" && !progress
        ? "Starting pipeline"
        : prettyTool(runningTool.name))
    : text ? "Answering" : "Thinking";

  return (
    <div className="message assistant">
      <span className="msg-avatar" aria-hidden="true">AI</span>
      <div className="msg-body">
        <div className="role">Clerk</div>
        {/* The trace stays live until the final message replaces this turn. */}
        <ThinkingTrace
          label={label}
          since={since}
          live
          steps={tools.map((t, i) => ({
            id: `${t.name}-${i}`,
            label: `${prettyTool(t.name)}${toolArg(t.input) ? ` · ${toolArg(t.input)}` : ""}`,
            state: t.status,
          }))}
        />
        {progress && <PipelineTasks progress={progress} />}
        {text && (
          <div className="content streaming">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents(onCitationClick)}>
              {text}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}


/** Live per-step view of a pipeline run in flight — fed by the clerk's
 *  pipeline_progress snapshots. */
function PipelineTasks({ progress }: { progress: PipelineProgress }) {
  const steps = progress.nodes.filter(n => n.kind !== "template");
  const done = steps.filter(n => n.status === "done").length;
  return (
    <TaskCard
      title={<>Pipeline <code>{progress.pipeline_id}</code></>}
      badge={<Pill tone={done === steps.length ? "done" : "running"} dot={done < steps.length}>
        {done}/{steps.length}
      </Pill>}
      rows={steps.map(n => ({
        id: n.id,
        glyph: glyphOf(n.kind || ""),
        label: stepOf(n.kind || "", n.label || n.id),
        state: n.status as RunState,
      }))}
    />
  );
}


/** Finished tool calls on a past turn — quiet by default, one click to read. */
function ToolTrace({ tools }: { tools: PendingTool[] }) {
  const failed = tools.filter(t => t.status === "error").length;
  return (
    <ThinkingTrace
      label={
        <>
          {tools.length} tool call{tools.length === 1 ? "" : "s"}
          {failed > 0 && <> · <span style={{ color: "var(--red)" }}>{failed} failed</span></>}
        </>
      }
      steps={tools.map((t, i) => ({
        id: `${t.name}-${i}`,
        label: `${prettyTool(t.name)}${toolArg(t.input) ? ` · ${toolArg(t.input)}` : ""}`,
        state: t.status,
      }))}
    />
  );
}

/** Tool ids are machine names; the transcript shows the readable form. */
function prettyTool(name: string): string {
  const s = name.replace(/^skill_/, "").replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The one argument worth surfacing beside a tool call — nothing, if none is. */
function toolArg(input: unknown): string {
  if (!input || typeof input !== "object") return "";
  const obj = input as Record<string, unknown>;
  const pick = obj.query ?? obj.page_id ?? obj.entity_ref ?? obj.pipeline_id;
  if (typeof pick === "string") return pick;
  return pick === undefined ? "" : JSON.stringify(pick);
}

function isSynapseId(s: string): boolean {
  return /^(canon|playbooks|entities|worked_examples|partners|source_library|concepts|records|faq)\//.test(s);
}

/** Split a citation href into its synapse page id + optional section anchor. */
function parseCitation(href: string): { pageId: string; anchor?: string } | null {
  const [id, anchor] = href.replace(/^#/, "").split("#");
  return id && isSynapseId(id) ? { pageId: id, anchor: anchor || undefined } : null;
}

/** Markdown renderers shared by the final + streaming views. Citation links
 *  (and bare-backtick page ids, as a fallback) become clickable chips that
 *  open the synapse preview; everything else opens in a new tab. */
function mdComponents(onCitationClick: (pageId: string, anchor?: string) => void): Components {
  return {
    a({ href, children }) {
      const cite = href ? parseCitation(href) : null;
      if (cite) {
        return (
          <a
            href={`#${cite.pageId}`}
            className="citation"
            onClick={e => { e.preventDefault(); onCitationClick(cite.pageId, cite.anchor); }}
          >
            {children}
          </a>
        );
      }
      return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
    },
    code({ className, children }) {
      const text = String(children ?? "");
      // Safety net: a page id the clerk left in backticks instead of a link.
      if (!className && !text.includes("\n") && isSynapseId(text)) {
        return (
          <a
            href={`#${text}`}
            className="citation"
            onClick={e => { e.preventDefault(); onCitationClick(text); }}
          >
            {text}
          </a>
        );
      }
      return <code className={className}>{children}</code>;
    },
  };
}
